import { toSentenceCase } from "utils/string";

export const VIEW_BY_AREA = "by-area";
export const VIEW_BY_USER = "by-user";

export const VIEW_OPTIONS = [
  { label: "By Area", value: VIEW_BY_AREA },
  { label: "By User", value: VIEW_BY_USER },
];

export const AREA_TINTS = {
  BD_CAPTURE: "var(--token-surface-primary-150)",
  PROPOSALS: "var(--token-surface-success-125)",
  CONTRACTS: "var(--token-surface-warning-125)",
  MISC_ANALYSIS: "var(--token-surface-error-125)",
  CONFIG: "var(--token-surface-information-125)",
  VT_DOCS_LEGACY: "var(--token-surface-gray-150)",
};

export const AREA_ACCENTS = {
  BD_CAPTURE: "var(--token-surface-primary-600)",
  PROPOSALS: "var(--token-surface-success-800)",
  CONTRACTS: "var(--token-surface-warning-700)",
  MISC_ANALYSIS: "var(--token-surface-error-600)",
  CONFIG: "var(--token-surface-information-600)",
  VT_DOCS_LEGACY: "var(--token-surface-gray-600)",
};

export const totalTint = (areaKey) =>
  `color-mix(in srgb, ${AREA_TINTS[areaKey]} 82%, ${AREA_ACCENTS[areaKey]} 10%)`;

export const LAYOUT_DETAILED = "detailed";
export const LAYOUT_TOTALS = "totals";

export const LAYOUT_OPTIONS = [
  { label: "Totals Only", value: LAYOUT_TOTALS },
  { label: "Detailed", value: LAYOUT_DETAILED },
];

export const AREA_LABELS = {
  BD_CAPTURE: "BD / Capture",
  PROPOSALS: "Proposals",
  MISC_ANALYSIS: "Misc Analysis",
  CONTRACTS: "Contracts",
  CONFIG: "Config",
  VT_DOCS_LEGACY: "VT Docs (Legacy)",
};

export const AREA_COLORS = {
  BD_CAPTURE: "#6366f1",
  PROPOSALS: "#4ADE80",
  MISC_ANALYSIS: "#6b7280",
  CONTRACTS: "#F59F00",
  CONFIG: "#B19DFB",
  VT_DOCS_LEGACY: "#22D3EE",
};

export const AREA_ORDER = [
  "BD_CAPTURE",
  "PROPOSALS",
  "CONTRACTS",
  "MISC_ANALYSIS",
  "CONFIG",
  "VT_DOCS_LEGACY",
];

export const DISPLAY_SUBAREAS_BY_AREA = {
  BD_CAPTURE: [
    "SHREDDING",
    "COMPARE_DOCS",
    "COMPARE_EXCEL",
    "TECHNICAL_ALIGNMENT",
    "DOCUMENT_EDITING",
    "SAVED_SEARCHES",
    "VISI",
    "OPPORTUNITY_TRACKING",
    "OPPORTUNITY_SEARCH",
  ],
  PROPOSALS: [
    "SHREDDING",
    "COMPARE_DOCS",
    "COMPARE_EXCEL",
    "ACRONYM",
    "DOCUMENT_EDITING",
    "OUTLINE",
    "VISI",
    "UPLOAD",
  ],
  CONTRACTS: ["CONTRACT_CREATED", "CONTRACT_ARCHIVED"],
  MISC_ANALYSIS: [
    "SHREDDING",
    "COMPARE_DOCS",
    "COMPARE_EXCEL",
    "ACRONYM",
    "DOCUMENT_EDITING",
    "VISI",
  ],
  CONFIG: ["DICTIONARIES", "COLLECTIONS", "PROMPTS", "WATCHWORDS"],
  VT_DOCS_LEGACY: [
    "UPLOAD",
    "COMPARE_DOCS",
    "COMPARE_EXCEL",
    "SIMPLE_SHRED",
    "CATEGORY_SHRED",
    "ACRONYM",
    "ANNOTATED_DOC",
    "DICTIONARY_SEARCH",
    "DISCOVERY",
    "READABILITY_PDF",
    "READABILITY_EXCEL",
  ],
};

export const SUB_AREA_LABELS = {
  BD_CAPTURE: {
    SHREDDING: "Shred",
    COMPARE_DOCS: "Compare Docs",
    COMPARE_EXCEL: "Compare Excel",
    COMPARE: "Compare Docs/Excel",
    TECHNICAL_ALIGNMENT: "Technical alignment",
    DOCUMENT_EDITING: "Document Editing",
    SAVED_SEARCHES: "Saved searches",
    VISI: "Visi Query",
    OPPORTUNITY_TRACKING: "Tracked opportunities",
    OPPORTUNITY_SEARCH: "Opportunity search",
    RESEARCH: "Research",
    AMENDMENT: "Amendment",
    BUSINESS_ALIGNMENT: "Business Alignment",
    CONVERSION: "Conversion",
  },
  PROPOSALS: {
    SHREDDING: "Shred",
    COMPARE_DOCS: "Compare Docs",
    COMPARE_EXCEL: "Compare Excel",
    COMPARE: "Compare Docs/Excel",
    ACRONYM: "Acronym",
    AMENDMENT: "Amendment",
    DOCUMENT_EDITING: "Document Editing",
    OUTLINE: "Outline",
    OVERVIEW: "Overview",
    VISI: "Visi Query",
    BUSINESS_ALIGNMENT: "Business Alignment",
    KANBAN: "Kanban",
  },
  MISC_ANALYSIS: {
    SHREDDING: "Shred",
    COMPARE_DOCS: "Compare Docs",
    COMPARE_EXCEL: "Compare Excel",
    COMPARE: "Compare Docs/Excel",
    ACRONYM: "Acronym",
    AMENDMENT: "Amendment",
    DOCUMENT_EDITING: "Document Editing",
    OVERVIEW: "Overview",
    VISI: "Visi Query",
  },
  CONTRACTS: {
    CONTRACT_CREATED: "Contracts Created",
    CONTRACT_ARCHIVED: "Archived Contracts",
    SHREDDING: "Dictionary Search",
    DOCUMENT_EDITING: "Document Editing",
    OVERVIEW: "Overview",
    MANAGEMENT: "Management",
    VISI: "Visi Query",
  },
  CONFIG: {
    COLLECTIONS: "Collections",
    DICTIONARIES: "Dictionaries",
    PROMPTS: "Prompts",
    WATCHWORDS: "Watchwords",
  },
  VT_DOCS_LEGACY: {
    UPLOAD: "File Add",
    COMPARE_DOCS: "Compare Docs",
    COMPARE_EXCEL: "Compare Excel",
    COMPARE: "Compare Docs/Excel",
    SIMPLE_SHRED: "Simple Shred",
    CATEGORY_SHRED: "Category Shred",
    ACRONYM: "Acronym",
    ANNOTATED_DOC: "Annotated Doc",
    DICTIONARY_SEARCH: "Dictionary Search",
    DISCOVERY: "Discovery",
    READABILITY_PDF: "Readability PDF",
    READABILITY_EXCEL: "Readability Excel",
    SHREDDING: "Shred",
    DOCUMENT_ANALYSIS: "Document analysis",
    FOLDER_ANALYSIS: "Folder Analysis",
    PLAIN_LANGUAGE: "Plain Language",
    STRUCTURE: "Structure",
    DICTIONARY_ASSOCIATION: "Dictionary Association",
    RECHECK: "Rechecks",
    EXPORT: "Exports",
    UNMAPPED: "Unmapped",
  },
};

export const WORKSPACE_VOLUME_TO_AREA = {
  TRACKED_OPPORTUNITIES: "BD_CAPTURE",
  PROPOSALS_STARTED: "PROPOSALS",
  ANALYSES_CREATED: "MISC_ANALYSIS",
};

const humanizeCode = (code) => {
  if (!code) {
    return "";
  }
  return code.split("_").map(toSentenceCase).join(" ");
};

export const getAreaLabel = (areaCode) =>
  AREA_LABELS[areaCode] ?? humanizeCode(areaCode);

export const getAreaColor = (areaCode) => AREA_COLORS[areaCode] ?? "#6b7280";

export const getSubAreaLabel = (areaCode, subAreaCode) =>
  SUB_AREA_LABELS[areaCode]?.[subAreaCode] ?? humanizeCode(subAreaCode);

export const EVENT_TYPE_LABELS = {
  STATUS_CHANGE: "Status Change",
  DOCUMENT: "Document",
  COMMENT: "Comment",
  ASSIGNMENT: "Assignment",
};

export const getEventTypeLabel = (type) =>
  EVENT_TYPE_LABELS[type] ?? humanizeCode(type ?? "Unknown");

export const EVENT_TYPE_PILL_COLORS = {
  STATUS_CHANGE: "var(--token-surface-information-150)",
  DOCUMENT: "var(--token-surface-primary-150)",
  ASSIGNMENT: "var(--token-surface-success-200)",
};

export const EVENT_TYPE_PILL_FALLBACK_COLOR = "var(--token-surface-gray-150)";

export const getEventTypePillColor = (type) =>
  EVENT_TYPE_PILL_COLORS[type] ?? EVENT_TYPE_PILL_FALLBACK_COLOR;
